import { useEffect, useRef } from 'react'
import { useAppDispatch } from '~/app/hooks'
import { PATH_NAME } from '~/constants'
import { logout } from '~/redux/features'
import _debounce from 'lodash/debounce'
import { LocalStorageKeys } from '~/constants/localStorageKeys'

export const useIdleTimer = (timeoutMs = 3600000) => {
  // Default timeout is 1 hour
  const dispatch = useAppDispatch()
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const visibliltyCallBack = () => {
    if (document.visibilityState === 'visible') {
      tryTriggerLogout() //when the document comes back into visibility immediately try and log out, if the timer hasnt elapsed nothing will happen
      if (timeoutRef.current == null) {
        resetTimer() //in case the existing timeout has fired and not been reattached
      }
    }
  }

  window.removeEventListener('visibilitychange', visibliltyCallBack) //clean up any existing ones on re-render
  window.addEventListener('visibilitychange', visibliltyCallBack)

  const wouldLogout = (): boolean => {
    const timestamp = localStorage.getItem(LocalStorageKeys.idleTimer)
    if (timestamp) {
      const now = new Date()
      let then = new Date(timestamp)
      then.setMilliseconds(then.getMilliseconds() + timeoutMs)
      if (now > then) {
        return true
      }
    }
    return false
  }
  const tryTriggerLogout = (force: boolean = false) => {
    if (wouldLogout() || force) {
      dispatch(logout())
    }
  }

  const resetTimer = () => {
    localStorage.setItem(LocalStorageKeys.idleTimer, new Date().toISOString())

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null //Lets also keep the ref state in sync
    }
    if (window.location.pathname !== PATH_NAME.LOGIN_PAGE.path) {
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null //The timeout is fired so remove the ref
        tryTriggerLogout()
      }, timeoutMs)
    }
  }

  const debouncedResetTimer = _debounce(resetTimer, 500, {
    trailing: true,
    leading: true
  })

  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart']
    events.forEach(event => window.addEventListener(event, debouncedResetTimer))

    // Reset the timer on path change
    debouncedResetTimer()

    return () => {
      events.forEach(event => window.removeEventListener(event, debouncedResetTimer))
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname])

  return resetTimer
}